import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  EyeIcon,
  UserIcon,
  CalendarIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { mutualisteAPI } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import BeneficiaryDetailsModal from '../components/Beneficiaries/BeneficiaryDetailsModal';
import EditBeneficiaryModal from '../components/Beneficiaries/EditBeneficiaryModal';
import AddAyantDroitModal from '../components/Beneficiaries/AddAyantDroitModal';
import toast from 'react-hot-toast';

const Beneficiaries = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [beneficiaryToEdit, setBeneficiaryToEdit] = useState(null);

  // Récupérer le profil du mutualiste connecté avec ses ayants-droit
  const { data: profileData, isLoading } = useQuery(
    ['mutualiste-profile', user?.id],
    () => mutualisteAPI.getProfile(),
    {
      enabled: !!user?.mutualisteId,
      staleTime: 0, // Toujours considérer les données comme périmées
      cacheTime: 5 * 60 * 1000 // Garder en cache 5 minutes
    }
  );

  // Récupérer les ayants-droit séparément pour plus de détails
  const { data: ayantsDroitData } = useQuery(
    ['ayants-droit', profileData?.data?.id],
    () => mutualisteAPI.getAyantsDroit(profileData.data.id),
    {
      enabled: !!profileData?.data?.id,
      staleTime: 0, // Toujours considérer les données comme périmées
      cacheTime: 5 * 60 * 1000 // Garder en cache 5 minutes
    }
  );

  const mutualiste = profileData?.data;
  const ayantsDroit = ayantsDroitData?.data || [];

  const getSafeLabel = (value) => {
    if (!value) return 'Non spécifié';

    if (typeof value === 'string' || typeof value === 'number') {
      return value;
    }

    if (typeof value === 'object') {
      const lib = value.libelle;

      if (typeof lib === 'string' || typeof lib === 'number') {
        return lib;
      }

      if (lib && typeof lib === 'object') {
        const nestedLib = lib.libelle;
        if (typeof nestedLib === 'string' || typeof nestedLib === 'number') {
          return nestedLib;
        }
      }

      return 'Non spécifié';
    }

    return String(value);
  };

  // Calculer l'âge
  const calculateAge = (dateNaissance) => {
    if (!dateNaissance) return 'N/A';
    const today = new Date();
    const birthDate = new Date(dateNaissance);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Obtenir la couleur du statut (codes internes + libellés français)
  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIF':
      case 'Actif(ve)':
        return 'bg-green-100 text-green-800';
      case 'INACTIF':
      case 'Radié(e)':
        return 'bg-red-100 text-red-800';
      case 'SUSPENDU':
      case 'Suspendu(e)':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Obtenir l'icône du genre
  const getGenderIcon = (genre) => {
    return <UserIcon className="h-5 w-5" />;
  };

  // Gestion des actions
  const handleViewDetails = (beneficiary) => {
    setSelectedBeneficiary(beneficiary);
    setShowDetailsModal(true);
  };

  const handleEdit = (beneficiary) => {
    setBeneficiaryToEdit(beneficiary);
    setShowEditModal(true);
  };

  const handleDelete = async (beneficiary) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${beneficiary.nom} ${beneficiary.prenoms} ?`)) {
      try {
        // TODO: Implémenter la suppression d'ayant-droit
        toast.success(`Suppression de ${beneficiary.nom} ${beneficiary.prenoms} (à implémenter)`);
      } catch (error) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const handleSuccess = () => {
    // Rafraîchir les données
    queryClient.invalidateQueries('mutualiste-profile');
    queryClient.invalidateQueries(['ayants-droit', profileData?.data?.id]);
  };

  // Actions pour l'adhérent
  const adherentActions = [
    {
      label: 'Voir détails',
      icon: EyeIcon,
      onClick: handleViewDetails,
      color: 'text-blue-600 hover:text-blue-800'
    },
    {
      label: 'Modifier',
      icon: PencilIcon,
      onClick: handleEdit,
      color: 'text-green-600 hover:text-green-800'
    }
  ];

  // Actions pour les ayants-droit
  const ayantDroitActions = [
    {
      label: 'Voir détails',
      icon: EyeIcon,
      onClick: handleViewDetails,
      color: 'text-blue-600 hover:text-blue-800'
    },
    {
      label: 'Modifier',
      icon: PencilIcon,
      onClick: handleEdit,
      color: 'text-green-600 hover:text-green-800'
    },
    {
      label: 'Supprimer',
      icon: TrashIcon,
      onClick: handleDelete,
      color: 'text-red-600 hover:text-red-800'
    }
  ];

  const handleAddAyantDroit = () => {
    setShowAddModal(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!mutualiste) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Impossible de charger les informations du mutualiste</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary-900 mb-2">
              Mes Bénéficiaires
            </h1>
            <p className="text-gray-600">
              Gérez votre profil d'adhérent et vos ayants-droit
            </p>
          </div>
          <button
            onClick={handleAddAyantDroit}
            className="btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Ajouter un ayant-droit</span>
          </button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UserIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total bénéficiaires</p>
              <p className="text-2xl font-bold text-gray-900">{1 + ayantsDroit.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UserIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Adhérent principal</p>
              <p className="text-2xl font-bold text-gray-900">1</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UserIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Ayants-droit</p>
              <p className="text-2xl font-bold text-gray-900">{ayantsDroit.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Adhérent principal */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <h2 className="text-xl font-semibold text-primary-900 mb-6">
          Adhérent Principal
        </h2>
        
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg p-6 border-l-4 border-primary-500">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="h-16 w-16 rounded-full bg-primary-600 flex items-center justify-center">
                  <span className="text-xl font-bold text-white">
                    {mutualiste.prenoms?.[0]}{mutualiste.nom?.[0]}
                  </span>
                </div>
              </div>
              
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {mutualiste.nom} {mutualiste.prenoms}
                  </h3>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(mutualiste.status?.libelle || mutualiste.status)}`}>
                    {mutualiste.status?.libelle || mutualiste.status || 'Non spécifié'}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-gray-600">
                  <div className="flex items-center space-x-2">
                    <UserIcon className="h-4 w-4" />
                    <span>Matricule: {mutualiste.matricule}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {getGenderIcon(mutualiste.genre)}
                    <span>{getSafeLabel(mutualiste.genre)} • {calculateAge(mutualiste.dateNaissance)} ans</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <CalendarIcon className="h-4 w-4" />
                    <span>Né(e) le {formatDate(mutualiste.dateNaissance)}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <MapPinIcon className="h-4 w-4" />
                    <span>{mutualiste.ville}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <PhoneIcon className="h-4 w-4" />
                    <span>{mutualiste.mobile1}</span>
                  </div>
                  
                  {mutualiste.enterprise && (
                    <div className="flex items-center space-x-2">
                      <BuildingOfficeIcon className="h-4 w-4" />
                      <span>{getSafeLabel(mutualiste.enterprise.nom || mutualiste.enterprise)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex space-x-2">
              {adherentActions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => action.onClick(mutualiste)}
                  className={`p-2 rounded-lg hover:bg-white/50 transition-colors ${action.color}`}
                  title={action.label}
                >
                  <action.icon className="h-5 w-5" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Ayants-droit */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-primary-900">
            Ayants-droit ({ayantsDroit.length})
          </h2>
        </div>
        
        {ayantsDroit.length === 0 ? (
          <div className="text-center py-8">
            <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun ayant-droit</h3>
            <p className="mt-1 text-sm text-gray-500">
              Commencez par ajouter un ayant-droit à votre couverture.
            </p>
            <div className="mt-6">
              <button
                onClick={handleAddAyantDroit}
                className="btn-primary flex items-center space-x-2 mx-auto"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Ajouter un ayant-droit</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ayantsDroit.map((ayant) => (
              <div key={ayant.id} className="bg-gray-50 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-full bg-blue-600 flex items-center justify-center">
                        <span className="text-sm font-bold text-white">
                          {ayant.prenoms?.[0]}{ayant.nom?.[0]}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 break-words">
                        {ayant.nom} {ayant.prenoms}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {ayant.lienParente?.libelle || ayant.lienParente}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2 text-xs text-gray-600 mb-4">
                  <div className="flex items-center space-x-2">
                    <UserIcon className="h-3 w-3" />
                    <span>{ayant.matricule}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {getGenderIcon(ayant.genre)}
                    <span>{getSafeLabel(ayant.genre)} • {calculateAge(ayant.dateNaissance)} ans</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <CalendarIcon className="h-3 w-3" />
                    <span>{formatDate(ayant.dateNaissance)}</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ayant.status?.libelle || ayant.status)}`}>
                    {ayant.status?.libelle || ayant.status || 'Non spécifié'}
                  </span>
                  
                  <div className="flex space-x-1">
                    {ayantDroitActions.map((action, index) => (
                      <button
                        key={index}
                        onClick={() => action.onClick(ayant)}
                        className={`p-1.5 rounded hover:bg-white transition-colors ${action.color}`}
                        title={action.label}
                      >
                        <action.icon className="h-4 w-4" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de détails */}
      <BeneficiaryDetailsModal
        beneficiary={selectedBeneficiary}
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedBeneficiary(null);
        }}
      />

      {/* Modal de modification */}
      <EditBeneficiaryModal
        beneficiary={beneficiaryToEdit}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setBeneficiaryToEdit(null);
        }}
        onSuccess={handleSuccess}
      />

      {/* Modal d'ajout d'ayant-droit */}
      <AddAyantDroitModal
        adherentId={mutualiste?.id}
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleSuccess}
      />
    </div>
  );
};

export default Beneficiaries;
